
import { createRoot } from 'react-dom/client';
import React, { StrictMode, Suspense } from 'react';
import App from './App.tsx';
import './index.css';

// Error boundary to catch JS errors during rendering
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("Application error:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="p-6 max-w-md bg-white rounded-lg shadow-lg">
            <h2 className="text-xl font-bold text-red-600 mb-3">Something went wrong</h2>
            <p className="text-gray-700 mb-4">
              The application encountered an error. Please try refreshing the page.
            </p>
            <button 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create root with ErrorBoundary to catch potential issues
const root = document.getElementById("root");

if (!root) {
  console.error("Root element not found");
} else {
  createRoot(root).render(
    <StrictMode>
      <ErrorBoundary>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <App />
        </Suspense>
      </ErrorBoundary>
    </StrictMode>
  );
}
